import { useEffect, useState } from 'react';
import { ConfigProvider, Tooltip } from 'antd';
import cn from 'classnames';

import { Icon, Text } from 'shared/components/ui';
import { SearchTagV2 } from 'features/search';
import { useGetCandidatesQuery, useGetCompanyCandidatesQuery, useGetCompanyCountQuery } from 'services';
import { SearchFormParams } from 'features/search';
import { CompanySearchFormParams } from 'features/technographics/models';

import styles from './index.module.scss';

type peopleFilter = 'locations' | 'countries' | 'seniority';
type companyFilter = 'city' | 'country' | 'company_domain' | 'website_keywords' | 'headcount';

interface Props {
	tags: string[];
	setTags: React.Dispatch<React.SetStateAction<string[]>>;
	filters?: boolean;
	peopleFilters?: [peopleFilter, string][];
	setPeopleFilters?: (values: React.SetStateAction<SearchFormParams>, shouldValidate?: boolean) => void;
	companiesFilters?: [companyFilter, string][];
	setCompaniesFilters?: (values: React.SetStateAction<CompanySearchFormParams>, shouldValidate?: boolean) => void;
	handleNext?: () => void;
	textNext?: string;
}

function formatNumberWithCommas(number?: number) {
	if (!number) return '';
	return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export const Sidebar = ({
	tags,
	setTags,
	filters,
	peopleFilters,
	setPeopleFilters,
	companiesFilters,
	setCompaniesFilters,
	handleNext,
	textNext
}: Props) => {
	const [debouncedTags, setDebouncedTags] = useState(tags);
	const [debouncedPeopleFilters, setDebouncedPeopleFilters] = useState<Record<string, string[]>>({});
	const [debouncedCompanyFilters, setDebouncedCompanyFilters] = useState<Record<string, string[]>>({});

	const [tagsOpen, setTagsOpen] = useState(true);
	const [filterTagsOpen, setFilterTagsOpen] = useState(true);

	const tooltipPlacement = 'topLeft';

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedTags(tags);
			setDebouncedPeopleFilters(groupFilters(peopleFilters || []));
			setDebouncedCompanyFilters(groupFilters(companiesFilters || []));
		}, 1000);

		return () => clearTimeout(handler);
	}, [tags, peopleFilters, companiesFilters]);

	function groupFilters(pairs: [string, string][]): Record<string, string[]> {
		const result: Record<string, string[]> = {};
		for (const [key, value] of pairs) {
			if (!result[key]) result[key] = [];
			result[key].push(value);
		}
		return result;
	}

	const { data: companyData, isFetching: isCompanyFetching } = useGetCompanyCandidatesQuery({
		skills: debouncedTags,
		...debouncedCompanyFilters
	});

	const { data, isFetching } = useGetCompanyCountQuery({
		skills: debouncedTags,
		...debouncedCompanyFilters
	});

	const { data: profileData, isFetching: isProfileFetching } = useGetCandidatesQuery({
		required_skills: debouncedTags,
		...debouncedPeopleFilters
	});

	const isPeopleSpecific =
		'locations' in debouncedPeopleFilters ||
		'countries' in debouncedPeopleFilters ||
		'seniority' in debouncedPeopleFilters;

	const handleRemoveTag = (tag: string) => () => {
		setTags(prevTags => prevTags!.filter(t => t !== tag));
	};

	return (
		<ConfigProvider
			theme={{
				components: {
					Tooltip: {
						paddingSM: 0,
						paddingXS: 0,
						borderRadius: 2,
						colorBgDefault: '#f8f8f8'
					}
				}
			}}>
			<div className={styles.sidebar}>
				<div className={styles.sidebarTitle}>
					<Text variant="inter/18/semi">Search Criteria</Text>
					<Text variant="inter/14/medium" color="grey">
						{tags.length + (companiesFilters?.length || 0) + (peopleFilters?.length || 0)}
					</Text>
				</div>
				<div className={styles.results}>
					<Text variant="inter/15/regular" color="darker_grey">
						{isProfileFetching || isFetching || isCompanyFetching ? 'Counting...' : 'Search results count'}
					</Text>
					<div className={styles.resultsCount}>
						<div className={cn(styles.resultsRow, styles.resultsRowCompanies)}>
							<div className={styles.resultsInline}>
								<Icon
									icon="buildings"
									size={24}
									className={
										isCompanyFetching ? styles.resultsIconLoading : styles.resultsIconCompanies
									}
								/>
								<div className={styles.resultsColumn}>
									<Text variant="inter/16/bold" color="green_new_dark">
										{isCompanyFetching
											? '...'
											: `${formatNumberWithCommas(companyData?.results_count)}`}
									</Text>
									<Text variant="inter/16/regular" color="green_new_dark">
										Company
									</Text>
								</div>
							</div>
							<Tooltip
								arrow={false}
								title={
									<div className={styles.tooltip}>
										<Text color="dark_green">
											Total number of companies that have developers with selected skills
										</Text>
									</div>
								}
								placement={tooltipPlacement}
								overlayClassName={styles.labelTooltipV2}>
								<Icon icon="info" className={styles.labelIcon} size={16} />
							</Tooltip>
						</div>
						<div className={cn(styles.resultsRow, styles.resultsRowPeople)}>
							<div className={styles.resultsInline}>
								<Icon
									icon="users"
									size={24}
									className={
										isProfileFetching || isFetching ? styles.resultsIconLoading : styles.resultsIcon
									}
								/>
								<div className={styles.resultsColumn}>
									<Text variant="inter/16/bold" color="blue_new_dark">
										{isProfileFetching || isFetching
											? '...'
											: `${formatNumberWithCommas(
													isPeopleSpecific ? profileData?.results_count : data?.results_count
											  )}`}
									</Text>
									<Text variant="inter/16/regular" color="blue_new_dark">
										People
									</Text>
								</div>
							</div>
							<Tooltip
								arrow={false}
								title={
									<div className={styles.tooltip}>
										<Text color="dark_green">
											Total number of developers with selected skills working in all companies
										</Text>
									</div>
								}
								placement={tooltipPlacement}
								overlayClassName={styles.labelTooltip}>
								<Icon icon="info" className={styles.labelIcon} size={16} />
							</Tooltip>
						</div>
					</div>
				</div>
				<div className={styles.filters}>
					<div className={styles.section}>
						<div className={styles.title}>
							<div className={styles.titleInline}>
								<Text variant="inter/15/semi">Technology skills</Text>
								<Text variant="inter/14/medium" color="grey">
									{tags.length}
								</Text>
							</div>
							<Icon
								icon="chevron"
								size={24}
								className={cn(styles.titleIcon, { [styles.titleIconOpen]: tagsOpen })}
								onClick={() => {
									setTagsOpen(open => !open);
								}}
							/>
						</div>
						{tagsOpen && (
							<div className={styles.tags}>
								{tags.map(tag => (
									<SearchTagV2 key={tag} text={tag} onClick={handleRemoveTag(tag)} card />
								))}
							</div>
						)}
					</div>
					{((peopleFilters && setPeopleFilters && peopleFilters.length > 0) ||
						(companiesFilters && setCompaniesFilters && companiesFilters.length > 0)) && (
						<div className={styles.section}>
							<div className={styles.title}>
								<div className={styles.titleInline}>
									<Text variant="inter/15/semi">Filters</Text>
									<Text variant="inter/14/medium" color="grey">
										{(companiesFilters?.length || 0) + (peopleFilters?.length || 0)}
									</Text>
								</div>
								<Icon
									icon="chevron"
									size={24}
									className={cn(styles.titleIcon, { [styles.titleIconOpen]: tagsOpen })}
									onClick={() => {
										setFilterTagsOpen(open => !open);
									}}
								/>
							</div>
							{filterTagsOpen && (
								<>
									{companiesFilters && setCompaniesFilters && companiesFilters.length > 0 && (
										<div className={styles.section}>
											<Text
												variant="inter/15/regular"
												color="grey"
												className={styles.sectionTitle}>
												Companies
											</Text>
											<div className={styles.tags}>
												{companiesFilters.map(([filter, tag]) => (
													<SearchTagV2
														key={tag}
														text={tag}
														onClick={() =>
															setCompaniesFilters(prev => {
																const newFilterValues =
																	prev[filter]?.filter(val => val !== tag) || [];
																const { [filter]: _, ...rest } = prev;
																return newFilterValues.length > 0
																	? { ...prev, [filter]: newFilterValues }
																	: rest;
															})
														}
														card
													/>
												))}
											</div>
										</div>
									)}
									{peopleFilters && setPeopleFilters && peopleFilters.length > 0 && (
										<div className={styles.section}>
											<Text
												variant="inter/15/regular"
												color="grey"
												className={styles.sectionTitle}>
												People
											</Text>
											<div className={styles.tags}>
												{peopleFilters.map(([filter, tag]) => (
													<SearchTagV2
														key={tag}
														text={tag}
														onClick={() =>
															setPeopleFilters(prev => {
																const newFilterValues =
																	prev[filter]?.filter(val => val !== tag) || [];
																const { [filter]: _, ...rest } = prev;
																return newFilterValues.length > 0
																	? { ...prev, [filter]: newFilterValues }
																	: rest;
															})
														}
														card
													/>
												))}
											</div>
										</div>
									)}
								</>
							)}
						</div>
					)}
				</div>
				{handleNext && textNext && (
					<div className={styles.footer}>
						<div onClick={handleNext} className={styles.button}>
							<Text variant="inter/15/semi" color="white">
								{textNext}
							</Text>
						</div>
					</div>
				)}
			</div>
		</ConfigProvider>
	);
};
